import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  FaUserGraduate,
  FaIdCard,
  FaPhone,
  FaEnvelope,
  FaBus,
  FaBed,
  FaCheck,
  FaUser,
  FaWalking,
  FaSun,
  FaUserShield,
  FaArrowLeft,
  FaCalendarAlt,
  FaUsers,
  FaMapMarkerAlt,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import useAuth from "../../useAuth";
import { server } from "../../server";
import compressImage from "../../utils/compressAsset";
import { classesList } from "../../jsonsFiles/classesList";

const styles = {
  sectionTitle:
    "text-sm font-black text-slate-800 dark:text-slate-100 border-b border-indigo-100 dark:border-slate-800 pb-1.5 mb-4 mt-5 flex items-center gap-2 uppercase tracking-wide",
  label: "block text-[11px] font-bold text-slate-600 dark:text-slate-300 mb-1 tracking-tight",
  input:
    "w-full px-3 py-1.5 text-xs border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all duration-150 bg-slate-50/50 dark:bg-slate-800/80 focus:bg-white dark:focus:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 hover:border-indigo-300 dark:hover:border-slate-600 font-semibold",
  select:
    "w-full px-3 py-1.5 text-xs border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-slate-50/50 dark:bg-slate-800/80 focus:bg-white dark:focus:bg-slate-900 text-slate-900 dark:text-white hover:border-indigo-300 dark:hover:border-slate-600 font-semibold",
  fileInput:
    "block w-full text-xs text-slate-500 dark:text-slate-400 file:mr-2.5 file:py-1 file:px-2.5 file:rounded-md file:border-0 file:text-[11px] file:font-bold file:bg-indigo-50 dark:file:bg-indigo-950/60 file:text-indigo-700 dark:file:text-indigo-300 hover:file:bg-indigo-100 transition-all cursor-pointer border border-slate-200 dark:border-slate-700 rounded-lg p-0.5 bg-white dark:bg-slate-800",
  error:
    "text-[10px] text-red-500 dark:text-red-400 flex items-center mt-0.5 font-bold",
  card: "bg-white dark:bg-slate-900 p-4 md:p-5 rounded-2xl shadow-xs border border-slate-100 dark:border-slate-800",
  radioLabel: "relative cursor-pointer group",
  radioInput: "peer sr-only",
  radioBox:
    "min-w-[2.2rem] px-2.5 h-8 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-black text-xs hover:border-indigo-300 peer-checked:bg-indigo-600 peer-checked:text-white peer-checked:border-indigo-600 transition-all duration-150 shadow-2xs",
};

const InputGroup = ({ label, children, error, required }) => (
  <div className="flex flex-col">
    <label className={styles.label}>
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative group">{children}</div>
    {error && <p className={styles.error}>{error.message}</p>}
  </div>
);

const FilePreview = ({ label, url, isImage }) => {
  if (!url || typeof url !== "string" || url === "") return null;
  return (
    <div className="mt-1.5 p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-[9px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-wider">
          Current {label}
        </span>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-indigo-600 dark:text-indigo-400 font-extrabold hover:underline"
        >
          Open
        </a>
      </div>
      {isImage ? (
        <img
          src={url}
          alt={label}
          className="w-full h-20 object-contain rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
        />
      ) : (
        <div className="p-1.5 flex items-center justify-center bg-white dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] font-bold text-slate-600 dark:text-slate-300 hover:text-indigo-600 truncate max-w-full"
          >
            View Document
          </a>
        </div>
      )}
    </div>
  );
};

export default function StudentRegistration() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(!!id);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      IsActive: true,
    },
  });

  const isEdit = !!id;
  const hasSLC = watch("HasSLC");
  const studentPhotoUrl = watch("studentPhoto");
  const studentAadhaarFileUrl = watch("studentAadhaarFile");
  const fatherPhotoUrl = watch("fatherPhoto");
  const fatherAadhaarFileUrl = watch("fatherAadhaarFile");
  const motherPhotoUrl = watch("motherPhoto");
  const motherAadhaarFileUrl = watch("motherAadhaarFile");
  const slcFileUrl = watch("slcFile");

  useEffect(() => {
    if (id && token) {
      const fetchStudent = async () => {
        try {
          const res = await axios.get(
            `${server}/students/getById?studentId=${id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          const rawData = res.data.data;
          const mappedData = {};
          Object.keys(rawData).forEach((key) => {
            mappedData[key] = rawData[key]; // keep original for watches
            const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
            mappedData[pascalKey] = rawData[key];
          });

          // Format DateOfBirth for input type="date"
          if (mappedData.DateOfBirth) {
            mappedData.DateOfBirth = mappedData.DateOfBirth.split("T")[0];
          }

          mappedData.StudentPANNo = rawData.studentPANNo || rawData.StudentPANNo || "";

          // Ensure HasSLC is correctly mapped just in case
          if ("hasSLC" in rawData) mappedData.HasSLC = rawData.hasSLC;

          reset(mappedData);
        } catch (error) {
          toast.error("Failed to load student for editing");
          navigate("/my/students");
        } finally {
          setLoading(false);
        }
      };
      fetchStudent();
    }
  }, [id, token, navigate, reset]);

  const onSubmit = async (data) => {
    try {
      const url = isEdit
        ? `${server}/students/updateById?studentId=${id}`
        : `${server}/students/register`;

      const method = isEdit ? "put" : "post";

      const formData = new FormData();
      for (const key of Object.keys(data)) {
        if (key.includes("PhotoFile")) {
          if (data[key] && data[key][0] instanceof File) {
            const compressedPhoto = await compressImage(data[key][0]);
            formData.append(key, compressedPhoto || data[key][0]);
          }
        } else if (key.includes("File") || key.includes("Photo")) {
          if (data[key] && data[key][0] instanceof File) {
            formData.append(key, data[key][0]);
          }
        } else {
          formData.append(key, data[key] === null ? "" : data[key]);
        }
      }

      await axios[method](url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(isEdit ? "Student updated!" : "Student registered!");
      navigate("/my/students");
    } catch (error) {
      toast.error(error.response?.data?.message || "Error saving student.");
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-slate-50 dark:bg-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400 mb-4"></div>
        <div className="text-slate-500 dark:text-slate-400 font-medium animate-pulse">
          Loading student record...
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      <div className="space-y-4">
        {/* Compact Header Bar */}
        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
              <FaUserGraduate size={18} />
            </div>
            <div>
              <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">
                {isEdit ? "Update Student Profile" : "New Student Registration"}
              </h1>
              <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">
                {isEdit
                  ? "Edit existing student admission and demographic records"
                  : "Enter details to register a new student"}
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate("/my/students")}
            className="bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-300 px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
          >
            <FaArrowLeft size={10} /> Back to Directory
          </button>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4 fade-in-up"
        >
          <div className={styles.card}>
            {/* Academic & Personal Information */}
            <h3 className={styles.sectionTitle}>
              <FaUserGraduate className="text-indigo-500 dark:text-indigo-400" />{" "}
              Academic & Personal Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3.5">
              <InputGroup
                label="Student Name"
                required
                error={errors.StudentName}
              >
                <div className="relative">
                  <FaUser className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs z-10" />
                  <input
                    {...register("StudentName", {
                      required: "Name is required",
                    })}
                    className={`${styles.input} pl-8`}
                    placeholder="e.g. Rahul Kumar"
                  />
                </div>
              </InputGroup>

              <InputGroup
                label="Date of Birth"
                required
                error={errors.DateOfBirth}
              >
                <input
                  type="date"
                  {...register("DateOfBirth", {
                    required: "Date of Birth is required",
                  })}
                  className={styles.input}
                  max={new Date()?.toISOString().split("T")[0]}
                />
              </InputGroup>

              <InputGroup label="Gender" required error={errors.Gender}>
                <div className="relative">
                  <FaUser className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs z-10" />
                  <select
                    {...register("Gender", { required: "Gender is required" })}
                    className={`${styles.select} pl-8`}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </InputGroup>

              <InputGroup
                label="Blood Group"
                required
                error={errors.BloodGroup}
              >
                <div className="relative">
                  <FaUsers className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs z-10" />
                  <select
                    {...register("BloodGroup", {
                      required: "Blood Group is required",
                    })}
                    className={`${styles.select} pl-8`}
                  >
                    <option value="">Unknown</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>
              </InputGroup>

              <InputGroup label="Category">
                <div className="relative">
                  <FaUsers className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs z-10" />
                  <select
                    {...register("Category")}
                    className={`${styles.select} pl-8`}
                  >
                    <option value="General">General</option>
                    <option value="OBC">OBC</option>
                    <option value="SC">SC</option>
                    <option value="ST">ST</option>
                  </select>
                </div>
              </InputGroup>

              <InputGroup
                label="Roll Number"
                required
                error={errors.RollNumber}
              >
                <input
                  {...register("RollNumber", {
                    required: "Roll Number is required",
                  })}
                  className={styles.input}
                  placeholder="e.g. 21"
                />
              </InputGroup>

              {/* Class Selection */}
              <div className="md:col-span-2 lg:col-span-3">
                <label className={styles.label}>
                  Class Selection <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {classesList.map((cls) => (
                    <label key={cls} className={styles.radioLabel}>
                      <input
                        type="radio"
                        value={cls}
                        {...register("Class", { required: "Required" })}
                        className={styles.radioInput}
                      />
                      <div className={styles.radioBox}>{cls}</div>
                    </label>
                  ))}
                </div>
                {errors.Class && (
                  <span className={styles.error}>{errors.Class.message}</span>
                )}
              </div>

              {/* Section Selection */}
              <div className="md:col-span-2 lg:col-span-1">
                <label className={styles.label}>
                  Section <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {["A", "B", "C", "D", "E"].map((sec) => (
                    <label key={sec} className={styles.radioLabel}>
                      <input
                        type="radio"
                        value={sec}
                        {...register("Section", { required: "Required" })}
                        className={styles.radioInput}
                      />
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black text-xs hover:border-indigo-200 peer-checked:bg-indigo-600 peer-checked:text-white peer-checked:border-indigo-600 transition-all duration-150">
                        {sec}
                      </div>
                    </label>
                  ))}
                </div>
                {errors.Section && (
                  <span className={styles.error}>{errors.Section.message}</span>
                )}
              </div>

              {/* SLC Checkbox */}
              <div className="md:col-span-2 lg:col-span-1 mt-2">
                <label
                  className={`flex flex-col gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${hasSLC ? "bg-amber-50 dark:bg-amber-950/30 border-amber-400 dark:border-amber-500/50 shadow-md" : "bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-700 hover:border-amber-200 dark:hover:border-slate-600"}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${hasSLC ? "bg-amber-500 border-amber-500" : "bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-600"}`}
                    >
                      {hasSLC && <FaCheck className="text-white text-xs" />}
                    </div>
                    <input
                      type="checkbox"
                      {...register("HasSLC")}
                      className="hidden"
                    />
                    <span
                      className={`font-bold text-sm transition-colors ${hasSLC ? "text-slate-800 dark:text-amber-300" : "text-slate-600 dark:text-slate-300"}`}
                    >
                      Has SLC / TC?
                    </span>
                  </div>
                  {hasSLC && (
                    <div className="animate-in fade-in slide-in-from-top-2 pt-2 border-t border-amber-200/50 dark:border-amber-800/50 mt-1">
                      <div className="flex flex-col">
                        <input
                          type="file"
                          {...register("SLCFileDetails", {
                            validate: {
                              required: (value) =>
                                !isEdit &&
                                hasSLC &&
                                (!value || value.length === 0)
                                  ? "SLC Document is required"
                                  : true,
                              size: (files) => {
                                if (!files || files.length === 0) return true;
                                return (
                                  files[0].size <= 307200 ||
                                  "Document must be less than 300 KB"
                                );
                              },
                            },
                          })}
                          accept="image/*"
                          className={`text-xs w-full file:bg-amber-100 dark:file:bg-amber-900 file:text-amber-700 dark:file:text-amber-300 file:mr-3 file:py-1 file:px-2 file:rounded-lg file:border-0 hover:file:bg-amber-200 transition-all cursor-pointer bg-white/50 dark:bg-slate-900/50 p-1 rounded-lg border ${errors.SLCFileDetails ? "border-red-500" : "border-amber-200 dark:border-amber-700/50"}`}
                        />
                        {errors.SLCFileDetails && (
                          <span className={`${styles.error} text-red-500`}>
                            {errors.SLCFileDetails.message}
                          </span>
                        )}
                        <FilePreview
                          label="SLC Doc"
                          url={slcFileUrl}
                          isImage={false}
                        />
                      </div>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Documents & Identification */}
            <h3 className={styles.sectionTitle}>
              <FaIdCard className="text-indigo-500 dark:text-indigo-400" />{" "}
              Documents & Identification
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3.5">
              <InputGroup
                label="Student Aadhaar Number"
                required
                error={errors.StudentAadhaarNo}
              >
                <input
                  {...register("StudentAadhaarNo", {
                    required: "Aadhaar number is required",
                    pattern: {
                      value: /^\d{12}$/,
                      message: "Aadhaar must be exactly 12 digits",
                    },
                  })}
                  className={styles.input}
                  placeholder="XXXX XXXX XXXX"
                />
              </InputGroup>

              <InputGroup
                label="Student PAN Number"
                error={errors.StudentPANNo}
              >
                <input
                  {...register("StudentPANNo", {
                    pattern: {
                      value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i,
                      message: "Invalid PAN format (e.g. ABCDE1234F)",
                    },
                    setValueAs: (v) => (v ? v.toUpperCase() : ""),
                  })}
                  className={`${styles.input} uppercase`}
                  placeholder="ABCDE1234F"
                  maxLength={10}
                />
              </InputGroup>

              <InputGroup
                label="Student Photograph (Max 1 MB)"
                error={errors.StudentPhotoFile}
              >
                <input
                  type="file"
                  {...register("StudentPhotoFile", {
                    validate: (files) => {
                      if (!files || files.length === 0) return true;
                      return (
                        files[0].size <= 1048576 ||
                        "Photo must be less than 1 MB"
                      );
                    },
                  })}
                  accept="image/*"
                  className={styles.fileInput}
                />
                <FilePreview
                  label="Photo"
                  url={studentPhotoUrl}
                  isImage={true}
                />
              </InputGroup>
              <div className="lg:col-span-2">
                <InputGroup
                  label="Upload Aadhaar Card (Max 1 MB)"
                  error={errors.StudentAadhaarFileDetails}
                >
                  <input
                    type="file"
                    {...register("StudentAadhaarFileDetails", {
                      validate: (files) => {
                        if (!files || files.length === 0) return true;
                        return (
                          files[0].size <= 1048576 ||
                          "File must be less than 1 MB"
                        );
                      },
                    })}
                    accept="image/*"
                    className={styles.fileInput}
                  />
                  <FilePreview
                    label="Aadhaar"
                    url={studentAadhaarFileUrl}
                    isImage={false}
                  />
                </InputGroup>
              </div>
            </div>

            {/* Family Details */}
            <h3 className={styles.sectionTitle}>
              <FaUsers className="text-indigo-500 dark:text-indigo-400" />{" "}
              Family Information
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Father */}
              <div className="bg-slate-50/50 dark:bg-slate-800/40 p-3.5 rounded-xl border border-slate-100 dark:border-slate-800 hover:border-blue-100 dark:hover:border-slate-700 transition-colors">
                <h4 className="font-extrabold text-xs text-slate-800 dark:text-slate-200 mb-2.5 flex items-center gap-1.5 uppercase tracking-wide">
                  <span className="p-1 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-md">
                    <FaUser size={12} />
                  </span>{" "}
                  Father's Details
                </h4>
                <div className="space-y-2.5">
                  <InputGroup
                    label="Father's Name"
                    required
                    error={errors.FatherName}
                  >
                    <input
                      {...register("FatherName", {
                        required: "Name is required",
                      })}
                      className={styles.input}
                      placeholder="Full Name"
                    />
                  </InputGroup>
                  <div className="grid grid-cols-2 gap-4">
                    <InputGroup
                      label="Mobile Number"
                      required
                      error={errors.FatherMobileNo}
                    >
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 text-xs font-bold">
                          +91
                        </span>
                        <input
                          {...register("FatherMobileNo", {
                            required: "Mobile Number is required",
                            pattern: {
                              value: /^[0-9]{10}$/,
                              message: "Must be 10 digits",
                            },
                          })}
                          className={`${styles.input} pl-8`}
                          placeholder="10-digit"
                          maxLength={10}
                        />
                      </div>
                    </InputGroup>
                    <InputGroup
                      label="Aadhaar No"
                      required
                      error={errors.FatherAadhaarNo}
                    >
                      <input
                        {...register("FatherAadhaarNo", {
                          required: "Aadhaar No is required",
                          pattern: {
                            value: /^\d{12}$/,
                            message: "Aadhaar must be exactly 12 digits",
                          },
                        })}
                        className={styles.input}
                        placeholder="Aadhaar No"
                      />
                    </InputGroup>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputGroup
                      label="Photograph (Max 1 MB)"
                      error={errors.FatherPhotoFile}
                    >
                      <input
                        type="file"
                        {...register("FatherPhotoFile", {
                          validate: (files) => {
                            if (!files || files.length === 0) return true;
                            return (
                              files[0].size <= 1048576 ||
                              "Photo must be less than 1 MB"
                            );
                          },
                        })}
                        accept="image/*"
                        className={styles.fileInput}
                      />
                      <FilePreview
                        label="Photo"
                        url={fatherPhotoUrl}
                        isImage={true}
                      />
                    </InputGroup>
                    <InputGroup
                      label="Upload Aadhaar (Max 1 MB)"
                      error={errors.FatherAadhaarFileDetails}
                    >
                      <input
                        type="file"
                        {...register("FatherAadhaarFileDetails", {
                          validate: (files) => {
                            if (!files || files.length === 0) return true;
                            return (
                              files[0].size <= 1048576 ||
                              "File must be less than 1 MB"
                            );
                          },
                        })}
                        accept="image/*"
                        className={styles.fileInput}
                      />
                      <FilePreview
                        label="Aadhaar"
                        url={fatherAadhaarFileUrl}
                        isImage={false}
                      />
                    </InputGroup>
                  </div>
                </div>
              </div>

              {/* Mother */}
              <div className="bg-slate-50/50 dark:bg-slate-800/40 p-3.5 rounded-xl border border-slate-100 dark:border-slate-800 hover:border-pink-100 dark:hover:border-slate-700 transition-colors">
                <h4 className="font-extrabold text-xs text-slate-800 dark:text-slate-200 mb-2.5 flex items-center gap-1.5 uppercase tracking-wide">
                  <span className="p-1 bg-pink-100 dark:bg-pink-900/50 text-pink-600 dark:text-pink-400 rounded-md">
                    <FaUser size={12} />
                  </span>{" "}
                  Mother's Details
                </h4>
                <div className="space-y-2.5">
                  <InputGroup
                    label="Mother's Name"
                    required
                    error={errors.MotherName}
                  >
                    <input
                      {...register("MotherName", {
                        required: "Name is required",
                      })}
                      className={styles.input}
                      placeholder="Full Name"
                    />
                  </InputGroup>
                  <div className="grid grid-cols-2 gap-4">
                    <InputGroup
                      label="Mobile Number"
                      required
                      error={errors.MotherMobileNo}
                    >
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 text-xs font-bold">
                          +91
                        </span>
                        <input
                          {...register("MotherMobileNo", {
                            required: "Mobile Number is required",
                            pattern: {
                              value: /^[0-9]{10}$/,
                              message: "Must be 10 digits",
                            },
                          })}
                          className={`${styles.input} pl-8`}
                          placeholder="10-digit"
                          maxLength={10}
                        />
                      </div>
                    </InputGroup>
                    <InputGroup
                      label="Aadhaar No"
                      required
                      error={errors.MotherAadhaarNo}
                    >
                      <input
                        {...register("MotherAadhaarNo", {
                          required: "Aadhaar No is required",
                          pattern: {
                            value: /^\d{12}$/,
                            message: "Aadhaar must be exactly 12 digits",
                          },
                        })}
                        className={styles.input}
                        placeholder="Aadhaar No"
                      />
                    </InputGroup>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputGroup
                      label="Photograph (Max 1 MB)"
                      error={errors.MotherPhotoFile}
                    >
                      <input
                        type="file"
                        {...register("MotherPhotoFile", {
                          validate: (files) => {
                            if (!files || files.length === 0) return true;
                            return (
                              files[0].size <= 1048576 ||
                              "Photo must be less than 1 MB"
                            );
                          },
                        })}
                        accept="image/*"
                        className={styles.fileInput}
                      />
                      <FilePreview
                        label="Photo"
                        url={motherPhotoUrl}
                        isImage={true}
                      />
                    </InputGroup>
                    <InputGroup
                      label="Upload Aadhaar (Max 1 MB)"
                      error={errors.MotherAadhaarFileDetails}
                    >
                      <input
                        type="file"
                        {...register("MotherAadhaarFileDetails", {
                          validate: (files) => {
                            if (!files || files.length === 0) return true;
                            return (
                              files[0].size <= 1048576 ||
                              "File must be less than 1 MB"
                            );
                          },
                        })}
                        accept="image/*"
                        className={styles.fileInput}
                      />
                      <FilePreview
                        label="Aadhaar"
                        url={motherAadhaarFileUrl}
                        isImage={false}
                      />
                    </InputGroup>
                  </div>
                </div>
              </div>
            </div>

            {/* Local Guardian */}
            <h3 className={styles.sectionTitle}>
              <FaUserShield className="text-indigo-500 dark:text-indigo-400" />{" "}
              Local Guardian & Logistics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3.5">
              <InputGroup
                label="Guardian Name"
                required
                error={errors.LocalGuardianName}
              >
                <input
                  {...register("LocalGuardianName", {
                    required: "Name is required",
                  })}
                  className={styles.input}
                  placeholder="Full Name"
                />
              </InputGroup>
              <InputGroup
                label="Guardian Mobile"
                required
                error={errors.LocalGuardianMobileNo}
              >
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 text-xs font-bold">
                    +91
                  </span>
                  <input
                    {...register("LocalGuardianMobileNo", {
                      required: "Mobile is required",
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: "Must be 10 digits",
                      },
                    })}
                    className={`${styles.input} pl-8`}
                    placeholder="10-digit"
                    maxLength={10}
                  />
                </div>
              </InputGroup>
              <InputGroup
                label="Primary SMS Contact"
                required
                error={errors.ParentContactNo}
              >
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 text-xs font-bold">
                    +91
                  </span>
                  <input
                    {...register("ParentContactNo", {
                      required: "Contact No is required",
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: "Must be 10 digits",
                      },
                    })}
                    className={`${styles.input} pl-8`}
                    placeholder="10-digit"
                    maxLength={10}
                  />
                </div>
              </InputGroup>
              <InputGroup
                label="Parent Email"
                required
                error={errors.ParentEmail}
              >
                <input
                  type="email"
                  {...register("ParentEmail", {
                    required: "Email is required",
                  })}
                  className={styles.input}
                  placeholder="email@example.com"
                />
              </InputGroup>
              <div className="md:col-span-2 lg:col-span-4 mt-1">
                <InputGroup
                  label="Home Address"
                  required
                  error={errors.Address}
                >
                  <textarea
                    {...register("Address", {
                      required: "Address is required",
                    })}
                    className={`${styles.input} min-h-[50px] resize-y py-1.5`}
                    placeholder="Enter complete residential address..."
                  />
                </InputGroup>
              </div>
            </div>

            {/* Transport Toggles */}
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
              {[
                {
                  name: "TransportRequired",
                  label: "Transport",
                  icon: FaBus,
                  activeColor:
                    "peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600 peer-checked:border-blue-600 dark:peer-checked:border-blue-500 peer-checked:shadow-sm peer-checked:hover:bg-blue-700",
                },
                {
                  name: "IsPedestrian",
                  label: "Pedestrian",
                  icon: FaWalking,
                  activeColor:
                    "peer-checked:bg-emerald-600 dark:peer-checked:bg-emerald-600 peer-checked:border-emerald-600 dark:peer-checked:border-emerald-500 peer-checked:shadow-sm peer-checked:hover:bg-emerald-700",
                },
                {
                  name: "IsDayBoarding",
                  label: "Day Boarding",
                  icon: FaSun,
                  activeColor:
                    "peer-checked:bg-amber-600 dark:peer-checked:bg-amber-600 peer-checked:border-amber-600 dark:peer-checked:border-amber-500 peer-checked:shadow-sm peer-checked:hover:bg-amber-700",
                },
                {
                  name: "HostelRequired",
                  label: "Hostel",
                  icon: FaBed,
                  activeColor:
                    "peer-checked:bg-purple-600 dark:peer-checked:bg-purple-600 peer-checked:border-purple-600 dark:peer-checked:border-purple-500 peer-checked:shadow-sm peer-checked:hover:bg-purple-700",
                },
                {
                  name: "TransportWithGuardian",
                  label: "Guardian",
                  icon: FaUserShield,
                  activeColor:
                    "peer-checked:bg-teal-600 dark:peer-checked:bg-teal-600 peer-checked:border-teal-600 dark:peer-checked:border-teal-500 peer-checked:shadow-sm peer-checked:hover:bg-teal-700",
                },
                {
                  name: "IsActive",
                  label: "Active",
                  icon: FaCheck,
                  activeColor:
                    "peer-checked:bg-green-600 dark:peer-checked:bg-green-600 peer-checked:border-green-600 dark:peer-checked:border-green-500 peer-checked:shadow-sm peer-checked:hover:bg-green-700",
                },
              ].map((opt) => (
                <label
                  key={opt.name}
                  className={`relative group ${
                    opt.name === "IsActive"
                      ? "cursor-not-allowed opacity-75"
                      : "cursor-pointer"
                  }`}
                  title={
                    opt.name === "IsActive"
                      ? "Student active status is enabled by default"
                      : undefined
                  }
                >
                  <input
                    type="checkbox"
                    {...register(opt.name)}
                    disabled={opt.name === "IsActive"}
                    className="peer sr-only"
                  />
                  <div
                    className={`flex flex-col items-center justify-center p-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 peer-checked:!text-white transition-all h-full ${
                      opt.activeColor
                    } ${
                      opt.name === "IsActive"
                        ? "pointer-events-none"
                        : "hover:bg-white dark:hover:bg-slate-800"
                    }`}
                  >
                    <opt.icon
                      className="text-base mb-1 transition-colors"
                    />
                    <span className="text-[10px] font-black uppercase tracking-wider text-center transition-colors">
                      {opt.label}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 bg-white dark:bg-slate-900 p-2.5 px-4 rounded-xl border border-slate-200 dark:border-slate-800 sticky bottom-3 z-20 shadow-sm">
            <button
              type="button"
              onClick={() => navigate("/my/students")}
              className="px-5 py-1.5 text-xs font-extrabold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-1.5 text-xs font-black text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-500 rounded-lg shadow-xs disabled:opacity-70 disabled:cursor-not-allowed transition-all flex items-center gap-1.5"
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin h-3.5 w-3.5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <FaCheck size={10} />
                  {isEdit ? "Update Student" : "Register Student"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
